import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, Clock, CheckCircle } from 'lucide-react';
import { format, isBefore } from 'date-fns';
import useBookings from '../hooks/useBookings';
import { updateBooking } from '../../supabase/bookings';
import { supabase } from '../../supabase';
import { toET } from '../../data/constants';

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-700',
};

const TIER_COLORS = {
  'The Foundation': 'bg-emerald-100 text-emerald-800',
  'The Alchemy': 'bg-blue-100 text-blue-800',
  'The Transformation': 'bg-yellow-100 text-yellow-800 border border-[#C9A84C]',
  maintenance: 'bg-yellow-100 text-yellow-800 border border-[#C9A84C]',
};

function exportCSV(bookings) {
  const rows = [
    ['Date', 'Client', 'Service', 'Price', 'Status', 'Type'],
    ...bookings.map(b => {
      const raw = b.scheduled_at || b.created_at;
      const d = raw ? new Date(raw) : null;
      return [
        d ? format(d, 'yyyy-MM-dd') : '',
        b.client_name || '',
        b.tier || b.service_type || '',
        b.price || 0,
        b.status || '',
        b.booking_type || '',
      ];
    }),
  ];
  const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bookings.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const TIME_TABS = ['All', 'Upcoming', 'Past'];

export default function Bookings() {
  const { bookings, loading } = useBookings();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeTab, setTimeTab] = useState('All');
  const [updating, setUpdating] = useState({});
  const filtered = useMemo(() => {
    const now = new Date();
    let list = [...bookings];

    // Time filter
    if (timeTab === 'Upcoming') {
      list = list.filter(b => {
        if (!b.scheduled_at) return false;
        const d = b.scheduled_at.toDate ? b.scheduled_at.toDate() : new Date(b.scheduled_at);
        return !isBefore(d, now);
      });
    } else if (timeTab === 'Past') {
      list = list.filter(b => {
        if (!b.scheduled_at) return false;
        const d = b.scheduled_at.toDate ? b.scheduled_at.toDate() : new Date(b.scheduled_at);
        return isBefore(d, now);
      });
    }

    if (statusFilter !== 'all') list = list.filter(b => b.status === statusFilter);

    // Upcoming first, then future dates descending; past at the bottom
    list.sort((a, b) => {
      const da = a.scheduled_at ? new Date(a.scheduled_at) : new Date(0);
      const db = b.scheduled_at ? new Date(b.scheduled_at) : new Date(0);
      return da - db;
    });

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(b =>
        (b.client_name || '').toLowerCase().includes(s) ||
        (b.tier || '').toLowerCase().includes(s) ||
        (b.service_type || '').toLowerCase().includes(s)
      );
    }

    return list;
  }, [bookings, search, statusFilter, timeTab]);

  const handleStatusChange = async (id, newStatus, booking) => {
    setUpdating(p => ({ ...p, [id]: true }));
    try {
      await updateBooking(id, { status: newStatus });
      await supabase.from('activity_log').insert({
        note: `Booking status → ${newStatus}: ${booking.client_name} (${booking.tier || booking.service_type || 'Service'})`,
        type: 'status_change',
        client_id: booking.client_id || null,
      });
    } finally {
      setUpdating(p => ({ ...p, [id]: false }));
    }
  };

  const now = new Date();
  // Summary stats
  const totalRevenue = filtered.reduce((s, b) => s + (b.price || 0), 0);
  const completedCount = filtered.filter(b => b.status === 'completed').length;

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Bookings</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCSV(filtered)}
            className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            <Download size={15} /> Export CSV
          </button>
          <Link
            to="/admin/bookings/new"
            className="flex items-center gap-1.5 bg-[#C9A84C] hover:bg-[#b8943d] text-black text-sm font-semibold px-4 py-1.5 rounded-lg"
          >
            + New Booking
          </Link>
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-xs text-gray-400">Showing</p>
          <p className="font-bold text-gray-900">{filtered.length}</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-xs text-gray-400">Completed</p>
          <p className="font-bold text-emerald-600">{completedCount}</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-xs text-gray-400">Revenue</p>
          <p className="font-bold text-[#C9A84C]">${totalRevenue.toLocaleString()}</p>
        </div>
      </div>

      {/* Time tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TIME_TABS.map(t => (
          <button
            key={t}
            onClick={() => setTimeTab(t)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              timeTab === t
                ? 'border-[#C9A84C] text-[#C9A84C]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t === 'Upcoming' && <Clock size={13} />}
            {t === 'Past' && <CheckCircle size={13} />}
            {t}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search client or service…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
        >
          <option value="all">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Service</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Price</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No bookings found.
                  </td>
                </tr>
              ) : (
                filtered.map(b => {
                  const rawDate = b.scheduled_at || b.created_at;
                  const dRaw = rawDate?.toDate ? rawDate.toDate() : (rawDate ? new Date(rawDate) : null);
                  const d = toET(dRaw);
                  const isEnrollmentDate = !b.scheduled_at && !!b.created_at;
                  const isPast = dRaw && isBefore(dRaw, now);
                  return (
                    <tr key={b.id} className={`hover:bg-gray-50 transition-colors ${isPast && b.status !== 'completed' ? 'opacity-75' : ''}`}>
                      <td className="px-4 py-3 text-gray-600">
                        <div className={isPast ? 'text-gray-400' : 'text-gray-700 font-medium'}>
                          {d ? format(d, 'MMM d, yyyy') : '—'}
                        </div>
                        {isEnrollmentDate
                          ? <div className="text-xs text-orange-400">Enrolled</div>
                          : d && <div className="text-xs text-gray-400">{format(d, 'h:mm a')}</div>
                        }
                      </td>
                      <td className="px-4 py-3">
                        {b.client_id ? (
                          <Link to={`/admin/clients/${b.client_id}`} className="font-medium text-gray-900 hover:text-[#C9A84C]">
                            {b.client_name}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-900">{b.client_name || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_COLORS[b.tier || b.service_type] || 'bg-gray-100 text-gray-600'}`}>
                          {b.tier || b.service_type || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          b.booking_type === 'maintenance'
                            ? 'bg-yellow-50 text-yellow-700'
                            : b.booking_type === 'one_time'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {b.booking_type === 'maintenance'
                            ? 'Maintenance'
                            : b.booking_type === 'one_time'
                            ? 'One-Time'
                            : 'Weekend'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell font-medium text-gray-900">
                        ${b.price || 0}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={b.status || 'scheduled'}
                          disabled={updating[b.id]}
                          onChange={e => handleStatusChange(b.id, e.target.value, b)}
                          className={`text-xs border-0 rounded-full px-2 py-1 font-medium focus:outline-none focus:ring-1 focus:ring-[#C9A84C] ${
                            STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/jobs/${b.id}`}
                          className="text-xs text-[#C9A84C] hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
